"use client";

import React, { useRef, useState, useCallback, useMemo } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation } from "swiper/modules";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import StockCard from "./StockCard";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";

/**
 * Props for SimilarStocksSlider component
 */
interface SimilarStocksSliderProps {
  /** Array of stock data to display */
  similarStocks: any[];
  /** Fallback image URL for stock logos */
  defaultImage?: string;
}

/**
 * Responsive carousel slider for displaying similar stocks
 * Features:
 * - Desktop: Arrow navigation with auto-hide when all slides are visible
 * - Mobile: Touch/swipe navigation
 * - Auto-sizing slides based on content
 */
const SimilarStocksSlider: React.FC<SimilarStocksSliderProps> = ({
  similarStocks,
  defaultImage,
}) => {
  // Media query hook for responsive behavior
  const isMobile = useMediaQuery("(max-width: 1024px)");

  // Navigation button refs
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  // Navigation state
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  /**
   * Initialize navigation refs before Swiper mounts
   */
  const handleBeforeInit = useCallback((swiper: SwiperType) => {
    if (typeof swiper.params.navigation !== "boolean" && swiper.params.navigation) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
  }, []);

  /**
   * Handle Swiper initialization and slide changes
   */
  const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper;
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  }, []);

  // Swiper configuration
  const swiperConfig = useMemo(
    () => ({
      modules: [Navigation, A11y],
      spaceBetween: 12,
      slidesPerView: "auto" as const,
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      resistanceRatio: 0.85,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: !isMobile
        ? {
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }
        : false,
    }),
    [isMobile]
  );

  // Early return if no stocks
  if (!similarStocks?.length) {
    return null;
  }

  return (
    <div className="relative">
      {/* Navigation buttons - always rendered on desktop */}
      {!isMobile && (
        <>
          <SwiperButton
            ref={prevRef}
            direction="prev"
            isDisabled={isAtStart}
            aria-label="Previous stocks"
            className="!-left-0"
          />
          <SwiperButton
            ref={nextRef}
            direction="next"
            isDisabled={isAtEnd}
            aria-label="Next stocks"
            className="!-right-0"
          />
        </>
      )}

      <Swiper
        {...swiperConfig}
        onBeforeInit={handleBeforeInit}
        onInit={handleSwiperUpdate}
        onSlideChange={handleSwiperUpdate}
        onResize={handleSwiperUpdate}
        onReachEnd={() => setIsAtEnd(true)}
        onFromEdge={() => setIsAtEnd(false)}
      >
        {similarStocks.map((stock, index) => (
          <SwiperSlide
            key={stock?.id || `stock-${index}`}
            style={{ width: "auto" }}
          >
            <StockCard stock={stock} defaultImage={defaultImage} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

SimilarStocksSlider.displayName = "SimilarStocksSlider";

export default SimilarStocksSlider;
